import { Download, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { useToast } from '../../../../components/toastContext';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { RecordReturnModal } from '../components/RecordReturnModal';
import { IssuanceModal } from '../components/IssuanceModal';
import { issuancesApi } from '../issuanceApi';
import {
  fetchIssuanceForm,
  issuanceFormDisabledReason,
  issuanceFormLabel,
  saveIssuanceForm,
} from '../issuanceForm';
import type { IssuanceDetail, IssuanceListItem } from '../issuanceTypes';
import {
  ISSUANCE_STATUS_LABEL,
  PROPERTY_CLASSIFICATION_LABEL,
  isCustody,
} from '../issuanceTypes';
import { inventoryItemsApi, storageFacilitiesApi } from '../inventoryApi';

/**
 * Stock leaving a store — dispensed, assigned into custody, or lent out.
 *
 * **The anchor is the NUMBER**, which is the item catalog's call rather than the facility's: an
 * issuance has no name, two releases on one day to one office are told apart by nothing else,
 * and the number is what a signed slip quotes.
 *
 * **The accountable officer is deliberately not a column.** The row carries a `personnelId`,
 * and turning that into a name is a two-hop through the identity store that would need
 * `personnel.read` for every reader of this list. The profile resolves it for the one record on
 * screen, which is the call `RealProperty`'s custodian already takes — so this list cannot be
 * searched or sorted by who signed for the stock, and that is a property of the split rather
 * than something to fix here.
 */
export function IssuancesIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [returning, setReturning] = useState<IssuanceDetail | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    issuance: IssuanceListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const toast = useToast();
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    Nothing can be issued without something in stock and somewhere to issue it from. Seeded TRUE
    and left true on a failed feed: flashing "receive stock first" on every load of a working
    module is worse than a banner arriving a moment late, and a request that did not come back
    is not evidence that a store is empty.
  */
  const [ready, setReady] = useState({ items: true, facilities: true });

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      inventoryItemsApi.options(controller.signal),
      storageFacilitiesApi.options(controller.signal),
    ])
      .then(([items, facilities]) => {
        if (controller.signal.aborted) return;
        setReady({ items: items.length > 0, facilities: facilities.length > 0 });
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const fetchIssuances = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      issuancesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          type: query.facets.type?.join(','),
          stage: query.facets.stage?.join(','),
          facility: query.facets.facility?.join(','),
          attention: query.facets.attention?.join(','),
        },
        signal,
      ),
    [],
  );

  // The row carries only what a list needs; the return form has to know the lines, so it
  // fetches the whole record first.
  const openReturn = useCallback(async (issuance: IssuanceListItem) => {
    setError(null);

    try {
      setReturning(await issuancesApi.get(issuance.id));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not open this issuance.', 0));
    }
  }, []);

  /**
   * Draws the slip's ICS or PAR on the server and saves it.
   *
   * **A failure is a TOAST, not the page's `error`** — that state is drawn only inside the
   * delete/restore dialog, so setting it here said nothing at the moment of the click and then
   * surfaced, stale, the next time somebody opened that dialog.
   */
  const downloadForm = useCallback(
    async (issuance: IssuanceListItem) => {
      if (issuance.acknowledgementType == null) return;

      const label = issuanceFormLabel(issuance.acknowledgementType);

      try {
        const { blob, fileName } = await fetchIssuanceForm(issuance.id);
        saveIssuanceForm(blob, fileName ?? `${label}-${issuance.controlNumber ?? issuance.issuanceNumber}.pdf`);
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : `Could not produce the ${label}.`);
      }
    },
    [toast],
  );

  const rowActions = useCallback(
    (issuance: IssuanceListItem): MenuItem[] => [
      /*
        ABSENT rather than disabled for a dispense — a consumable that was used up is not coming
        back, so the entry does not apply to that record at all. That is the hide-don't-disable
        rule read correctly: it is not withheld because of who is reading.
      */
      ...(isCustody(issuance.issuanceType)
        ? [
            {
              key: `return-${issuance.id}`,
              label: 'Record return',
              icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
              permission: 'issuances.return',
              disabledReason:
                issuance.outstandingQuantity > 0
                  ? null
                  : 'Everything on this issuance is already back.',
              onClick: () => void openReturn(issuance),
            } satisfies MenuItem,
          ]
        : []),
      /*
        THE SIGNED FORM — absent on a dispense, which signs none, and disabled for state on a
        deleted slip. Viewing and printing are on the profile's own tab; the list offers the
        file, which is what a reader reaching for it from a register wants.
      */
      ...(issuance.acknowledgementType != null
        ? [
            {
              key: `form-${issuance.id}`,
              label: `Download ${issuanceFormLabel(issuance.acknowledgementType)}`,
              icon: <Download className="h-4 w-4" aria-hidden="true" />,
              permission: 'issuances.read',
              disabledReason: issuanceFormDisabledReason(issuance),
              onClick: () => void downloadForm(issuance),
            } satisfies MenuItem,
          ]
        : []),
      {
        key: `restore-${issuance.id}`,
        label: 'Restore',
        permission: 'issuances.delete',
        disabledReason: issuance.isActive ? 'This issuance is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', issuance }),
      },
    ],
    [openReturn, downloadForm],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await issuancesApi.deactivate(confirming.issuance.id);
      else await issuancesApi.activate(confirming.issuance.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<IssuanceListItem>
        module="issuances"
        breadcrumb={['Logistics', 'Inventory', 'Issuances']}
        columns={COLUMNS}
        /*
          Filed by STAGE rather than the default status axis: the question a reader opens this
          list with is whether anything is still out, not whether a row is deleted. The reader
          repoints it from the rail's cog if they file differently, and that choice is
          remembered per module.
        */
        folderNavFacet="stage"
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'type', label: 'Method' },
          { key: 'stage', label: 'Stage' },
          { key: 'attention', label: 'Needs attention' },

          // GATED: this module's implied facility read is scoped to the write actions, so a
          // read-only reader genuinely lacks it and the gate hides something real.
          { key: 'facility', label: 'From store', permission: 'storage-facilities.read' },
        ]}
        prerequisites={[
          {
            label: 'an item with stock',
            met: ready.items,
            to: '/inventory/settings/items',
            permission: 'inventory-items.read',
          },
          {
            label: 'a storage facility',
            met: ready.facilities,
            to: '/inventory/settings/storage-facilities',
            permission: 'storage-facilities.read',
          },
        ]}
        /*
          FOUR types, because one issuance writes four kinds of row. A module history naming
          only the header would show a slip appearing and never say what moved — and the ledger
          line is what records where the stock went.
        */
        /*
          Every type this module's workflow WRITES, children included — a module whose list
          names only the parent hides every write to every child, and the failure is silent in
          the worst way: the row IS in `AuditLogs` and only the module's own history is blind.
          `StockMovement` is here because releasing stock writes the ledger.
        */
        auditEntityTypes={[
          'Issuance',
          'IssuanceItem',
          'IssuanceBatchAllocation',
          'IssuanceReturn',
          'StockMovement',
        ]}
        rowKey={(issuance) => issuance.id}
        fetch={fetchIssuances}
        reloadKey={reloadKey}
        searchPlaceholder="Search number, control number, reference, party or item"
        emptyMessage="Nothing has been issued yet. Release stock to start a register."
        onCreate={() => setCreating(true)}
        createPermission="issuances.create"
        onRowOpen={(issuance) => navigate(`/inventory/issuances/${issuance.id}`)}
        openPermission="issuances.read"
        rowActions={rowActions}
        onDelete={(issuance) => setConfirming({ kind: 'deactivate', issuance })}
        deletePermission="issuances.delete"
        deleteLabel={() => 'Delete'}
        /*
          Disabled for STATE with the count in the reason. The server refuses it too — the rule
          lives there — but meeting it as a 409 after a password prompt is a worse way to learn
          that deleting is not returning.
        */
        deleteDisabledReason={(issuance) =>
          !issuance.isActive
            ? 'This issuance is already deleted.'
            : issuance.outstandingQuantity > 0
              ? `${issuance.outstandingQuantity} of this issuance is still out. Record the return first — deleting the record would not bring the stock back.`
              : null
        }
        otherInfo={(issuance) => issuance}
      />

      {creating ? (
        <IssuanceModal
          open
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {returning ? (
        <RecordReturnModal
          open
          issuance={returning}
          onClose={() => setReturning(null)}
          onSaved={() => {
            setReturning(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete issuance' : 'Restore issuance'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`issuance-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `Issuance ${confirming.issuance.issuanceNumber} stops appearing in the register.`
                  : `Issuance ${confirming.issuance.issuanceNumber} appears in the register again.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  <b>This does not put the stock back.</b> Deleting corrects the register — a
                  slip keyed twice, or against the wrong record. If the property physically came
                  back, record a return instead.
                </p>
              ) : null}

              <p>
                The ledger is untouched either way — a movement is never deleted, so the history
                of what moved survives whatever happens to this row.
              </p>

              <p>Nothing is erased. A deleted issuance can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<IssuanceListItem>[] = [
  {
    key: 'issuanceNumber',
    header: 'Number',
    width: 150,
    sortable: true,
    render: (issuance) => <TextCell value={issuance.issuanceNumber} mono />,
  },
  {
    key: 'controlNumber',
    header: 'ICS / PAR no.',
    width: 140,
    summary: true,
    render: (issuance) => <TextCell value={issuance.controlNumber} mono />,
  },
  {
    key: 'issuanceType',
    header: 'Method',
    width: 110,
    sortable: true,
    summary: true,
    render: (issuance) => <TextCell value={issuance.issuanceTypeLabel} />,
  },
  {
    key: 'acknowledgementType',
    header: 'Form',
    width: 80,
    render: (issuance) => (
      <TextCell
        value={
          issuance.acknowledgementType === null
            ? null
            : PROPERTY_CLASSIFICATION_LABEL[issuance.acknowledgementType]
        }
        mono
      />
    ),
  },
  {
    key: 'issuedTo',
    header: 'Issued to',
    width: 200,
    flex: true,
    summary: true,
    /*
      One column for two mutually exclusive facts: an internal issuance names the receiving
      unit, a lend names the outside party. They can never both be set, so this is one value
      rather than a stacked cell.
    */
    render: (issuance) => (
      <TextCell value={issuance.receivingOrgUnitName ?? issuance.externalPartyName} />
    ),
  },
  {
    key: 'lineCount',
    header: 'Lines',
    width: 80,
    align: 'right',
    render: (issuance) => <TextCell value={String(issuance.lineCount)} mono numeric />,
  },
  {
    key: 'totalQuantity',
    header: 'Issued',
    width: 90,
    align: 'right',
    sortable: true,
    render: (issuance) => <TextCell value={String(issuance.totalQuantity)} mono numeric />,
  },
  {
    key: 'outstandingQuantity',
    header: 'Still out',
    width: 90,
    align: 'right',
    render: (issuance) => <TextCell value={String(issuance.outstandingQuantity)} mono numeric />,
  },
  {
    key: 'totalValue',
    header: 'Value',
    width: 120,
    align: 'right',
    /*
      An em dash rather than a zero where NOTHING on the slip was priced. An unpriced release
      and a free one are different facts — the same null-versus-zero line every other money
      column in this suite draws.
    */
    render: (issuance) => (
      <TextCell
        value={issuance.totalValue === null ? null : formatMoney(issuance.totalValue)}
        mono
        numeric
      />
    ),
  },
  {
    key: 'expectedReturnDate',
    header: 'Due back',
    width: 150,
    sortable: true,
    /*
      The OVERDUE marker rides on the date it is about, rather than taking a column of its own
      that would be blank on every dispense. It is the in-memory half of one rule — the rail's
      own overdue count reads the other half, and the two sit on this screen together.
    */
    render: (issuance) =>
      issuance.expectedReturnDate === null ? (
        <TextCell value={null} />
      ) : (
        <span className="flex items-center gap-1.5">
          <span className="font-mono text-xs text-slate-600">
            {formatDateOnly(issuance.expectedReturnDate)}
          </span>
          {issuance.isOverdue ? (
            <span className="text-xs font-medium text-red-600">· Overdue</span>
          ) : null}
        </span>
      ),
  },
  {
    key: 'stage',
    /** The record's own lifecycle — the shell sits it beside the status pill. */
    stage: true,
    header: 'Stage',
    width: 130,
    sortable: true,
    /*
      TWO state columns on one row, deliberately — the call the disciplinary panel already
      takes. This is the issuance's own lifecycle as plain text; the column below is the ROW's
      live/deleted marker, which is a different fact and the one a reader scans for.
    */
    render: (issuance) => (
      <StatusText tone="inactive">{ISSUANCE_STATUS_LABEL[issuance.status]}</StatusText>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (issuance) => (
      <StatusText tone={issuance.isActive ? 'active' : 'inactive'}>
        {issuance.isActive ? 'Active' : 'Deleted'}
      </StatusText>
    ),
  },
];
